import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Sparkles, ImageIcon, Wand2, Tag, BookOpen, Instagram, Facebook } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple, getBlogCategoryLabelKey, BLOG_CATEGORY_VALUES } from '../../i18n/localizeContent';

interface PixabayOption {
  pixabayId: number;
  previewURL: string;
  largeImageURL: string;
  tags: string;
}

interface SocialFormData {
  subject: string;
  category: string;
  hashtags: string[];
  targets: { blog: boolean; instagram: boolean; facebook: boolean };
  image: { source: 'pixabay' | 'ai' | 'manual' | 'none'; url: string; pixabayQuery: string; pixabayId?: number; aiPrompt: string };
}

const RedesSociaisForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEditing = Boolean(id);
  const notifications = useNotifications();

  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [formData, setFormData] = useState<SocialFormData>({
    subject: '',
    category: '',
    hashtags: [],
    targets: { blog: false, instagram: false, facebook: false },
    image: { source: 'none', url: '', pixabayQuery: '', aiPrompt: '' }
  });

  const [scheduleMode, setScheduleMode] = useState<'now' | 'schedule'>('now');
  const [scheduledAt, setScheduledAt] = useState('');

  const [loading, setLoading] = useState(false);
  const [generatingText, setGeneratingText] = useState(false);
  const [searchingImages, setSearchingImages] = useState(false);
  const [generatingAiImage, setGeneratingAiImage] = useState(false);
  const [hashtagInput, setHashtagInput] = useState('');
  const [pixabayOptions, setPixabayOptions] = useState<PixabayOption[]>([]);
  const [aiPromptInput, setAiPromptInput] = useState('');
  const [showAiImagePrompt, setShowAiImagePrompt] = useState(false);

  const fetchPost = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/redes-sociais/${id}`);
      const post = response.data;
      setFormData({
        subject: post.subject || '',
        category: post.category || '',
        hashtags: post.hashtags || [],
        targets: post.targets || { blog: false, instagram: false, facebook: false },
        image: post.image || { source: 'none', url: '', pixabayQuery: '', aiPrompt: '' }
      });
      setI18n({
        pt: {
          description: post.i18n?.pt?.description || post.description || '',
          excerpt: post.i18n?.pt?.excerpt || ''
        },
        en: {
          description: post.i18n?.en?.description || '',
          title: post.i18n?.en?.title || '',
          excerpt: post.i18n?.en?.excerpt || ''
        },
        es: {
          description: post.i18n?.es?.description || '',
          title: post.i18n?.es?.title || '',
          excerpt: post.i18n?.es?.excerpt || ''
        }
      });
      if (post.status === 'scheduled' && post.scheduledAt) {
        setScheduleMode('schedule');
        setScheduledAt(new Date(post.scheduledAt).toISOString().slice(0, 16));
      }
    } catch (error) {
      console.error('Erro ao carregar post de redes sociais:', error);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing && id) {
      fetchPost();
    }
  }, [isEditing, id, fetchPost]);

  const handleGenerateDescription = async () => {
    if (!formData.subject.trim()) {
      notifications.warning.validation('Informe o assunto antes de gerar a descrição.');
      return;
    }
    try {
      setGeneratingText(true);
      const response = await axios.post('/redes-sociais/generate-description', {
        subject: formData.subject,
        category: formData.category
      });
      const { description, excerpt, title, hashtags, imageQuery } = response.data;
      setI18n((prev) => ({
        pt: { ...prev.pt, description: description?.pt || '', excerpt: excerpt?.pt || '' },
        en: { ...prev.en, description: description?.en || '', excerpt: excerpt?.en || '', title: title?.en || '' },
        es: { ...prev.es, description: description?.es || '', excerpt: excerpt?.es || '', title: title?.es || '' }
      }));
      setFormData((prev) => ({
        ...prev,
        hashtags,
        image: { ...prev.image, pixabayQuery: imageQuery }
      }));
    } catch (error: any) {
      console.error('Erro ao gerar descrição:', error);
      notifications.error.socialPostGenerate(error.response?.data?.message || 'Erro ao gerar descrição e hashtags');
    } finally {
      setGeneratingText(false);
    }
  };

  const handleSearchPixabay = async () => {
    const query = formData.image.pixabayQuery || formData.subject;
    if (!query.trim()) {
      notifications.warning.validation('Informe o assunto ou gere a descrição antes de buscar imagens.');
      return;
    }
    try {
      setSearchingImages(true);
      const response = await axios.post('/redes-sociais/fetch-image-options', { subject: formData.subject, query });
      setPixabayOptions(response.data.options || []);
    } catch (error: any) {
      console.error('Erro ao buscar imagens no Pixabay:', error);
      notifications.error.socialPostImage(error.response?.data?.message || 'Erro ao buscar imagens');
    } finally {
      setSearchingImages(false);
    }
  };

  const handleSelectPixabayImage = async (option: PixabayOption) => {
    try {
      setSearchingImages(true);
      const response = await axios.post('/redes-sociais/select-image', {
        pixabayId: option.pixabayId,
        imageURL: option.largeImageURL
      });
      setFormData((prev) => ({
        ...prev,
        image: { ...prev.image, source: 'pixabay', url: response.data.url, pixabayId: option.pixabayId }
      }));
      setPixabayOptions([]);
    } catch (error: any) {
      console.error('Erro ao salvar imagem selecionada:', error);
      notifications.error.socialPostImage(error.response?.data?.message || 'Erro ao salvar imagem');
    } finally {
      setSearchingImages(false);
    }
  };

  const handleGenerateAiImage = async () => {
    if (!aiPromptInput.trim()) {
      notifications.warning.validation('Descreva a imagem que deseja gerar.');
      return;
    }
    try {
      setGeneratingAiImage(true);
      const response = await axios.post('/redes-sociais/generate-ai-image', { prompt: aiPromptInput });
      setFormData((prev) => ({
        ...prev,
        image: { ...prev.image, source: 'ai', url: response.data.url, aiPrompt: aiPromptInput }
      }));
      setShowAiImagePrompt(false);
    } catch (error: any) {
      console.error('Erro ao gerar imagem por IA:', error);
      notifications.error.socialPostImage(error.response?.data?.message || 'Erro ao gerar imagem por IA');
    } finally {
      setGeneratingAiImage(false);
    }
  };

  const handleAddHashtag = () => {
    const value = hashtagInput.trim().replace(/^#/, '');
    if (value && !formData.hashtags.includes(value)) {
      setFormData((prev) => ({ ...prev, hashtags: [...prev.hashtags, value] }));
      setHashtagInput('');
    }
  };

  const handleRemoveHashtag = (tag: string) => {
    setFormData((prev) => ({ ...prev, hashtags: prev.hashtags.filter((h) => h !== tag) }));
  };

  const toggleTarget = (target: keyof SocialFormData['targets']) => {
    setFormData((prev) => ({ ...prev, targets: { ...prev.targets, [target]: !prev.targets[target] } }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const ptDescription = i18n.pt?.description?.trim() || '';

    if (formData.subject.trim().length < 5) {
      notifications.warning.validation('Assunto deve ter pelo menos 5 caracteres');
      setLoading(false);
      return;
    }

    if (!ptDescription) {
      notifications.warning.validation('Gere ou escreva a descrição do post antes de salvar.');
      setLoading(false);
      return;
    }

    if (!formData.targets.blog && !formData.targets.instagram && !formData.targets.facebook) {
      notifications.warning.validation('Selecione ao menos um canal de publicação.');
      setLoading(false);
      return;
    }

    if (scheduleMode === 'schedule' && !scheduledAt) {
      notifications.warning.validation('Defina a data e hora do agendamento.');
      setLoading(false);
      return;
    }

    const payload = {
      subject: formData.subject.trim(),
      description: ptDescription,
      i18n,
      category: formData.category || 'geral',
      hashtags: formData.hashtags,
      targets: formData.targets,
      image: formData.image,
      status: scheduleMode === 'schedule' ? 'scheduled' : 'draft',
      scheduledAt: scheduleMode === 'schedule' ? scheduledAt : undefined
    };

    try {
      let postId = id;
      if (isEditing) {
        await axios.put(`/redes-sociais/${id}`, payload);
        notifications.success.socialPostUpdated();
      } else {
        const response = await axios.post('/redes-sociais', payload);
        postId = response.data._id;
        notifications.success.socialPostCreated();
      }

      if (scheduleMode === 'now' && postId) {
        await axios.post(`/redes-sociais/${postId}/publish-now`);
        notifications.success.socialPostPublished();
      }

      setTimeout(() => navigate('/admin/redes-sociais'), 1500);
    } catch (error: any) {
      console.error('Erro ao salvar post de redes sociais:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        notifications.error.socialPostCreate(errorMessages);
      } else {
        notifications.error.socialPostCreate(error.response?.data?.message || 'Erro ao salvar post');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/redes-sociais')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditing ? t('admin.socialPostEdit') : t('admin.socialPostNew')}
              </h1>
              <p className="text-gray-600">Gere descrição, hashtags e imagem, escolha os canais e publique ou agende.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Subject */}
              <div className="bg-white rounded-lg shadow p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Assunto</label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
                    placeholder="Ex: Novo processo de cidadania portuguesa por casamento"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateDescription}
                    disabled={generatingText}
                    className="btn-primary flex items-center justify-center space-x-2 whitespace-nowrap disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>{generatingText ? 'Gerando...' : 'Gerar descrição/hashtags'}</span>
                  </button>
                </div>
              </div>

              {/* Description (i18n) */}
              <div className="bg-white rounded-lg shadow p-6">
                <I18nTripleFields mode="social" value={i18n} onChange={setI18n} />
              </div>

              {/* Hashtags */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Hashtags</h3>
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={hashtagInput}
                      onChange={(e) => setHashtagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHashtag())}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Adicionar hashtag..."
                    />
                    <button
                      type="button"
                      onClick={handleAddHashtag}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <Tag className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.hashtags.map((tag) => (
                      <span key={tag} className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm">
                        #{tag}
                        <button type="button" onClick={() => handleRemoveHashtag(tag)} className="ml-2 text-primary-600 hover:text-primary-800">
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Image sourcing */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Imagem</h3>

                {formData.image.url && (
                  <div className="mb-4">
                    <img src={formData.image.url} alt="Imagem selecionada" className="h-40 w-full object-cover rounded-lg" />
                    <p className="text-xs text-gray-500 mt-1">Fonte: {formData.image.source}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mb-4">
                  <button
                    type="button"
                    onClick={handleSearchPixabay}
                    disabled={searchingImages}
                    className="btn-secondary flex items-center space-x-2 disabled:opacity-50"
                  >
                    <ImageIcon className="w-4 h-4" />
                    <span>{searchingImages ? 'Buscando...' : 'Buscar imagens (Pixabay)'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAiImagePrompt((prev) => !prev)}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <Wand2 className="w-4 h-4" />
                    <span>Gerar imagem com IA</span>
                  </button>
                </div>

                {showAiImagePrompt && (
                  <div className="flex items-center space-x-2 mb-4">
                    <input
                      type="text"
                      value={aiPromptInput}
                      onChange={(e) => setAiPromptInput(e.target.value)}
                      placeholder="Descreva a imagem que deseja gerar..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleGenerateAiImage}
                      disabled={generatingAiImage}
                      className="btn-primary whitespace-nowrap disabled:opacity-50"
                    >
                      {generatingAiImage ? 'Gerando...' : 'Gerar'}
                    </button>
                  </div>
                )}

                {pixabayOptions.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {pixabayOptions.map((option) => (
                      <button
                        key={option.pixabayId}
                        type="button"
                        onClick={() => handleSelectPixabayImage(option)}
                        className="rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-primary-500 transition-shadow"
                        title={option.tags}
                      >
                        <img src={option.previewURL} alt={option.tags} className="h-20 w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex flex-wrap justify-end gap-3">
                <button type="button" onClick={() => navigate('/admin/redes-sociais')} className="btn-secondary">
                  {t('common.cancel')}
                </button>
                <button type="submit" disabled={loading} className="btn-primary flex items-center space-x-2">
                  <Save className="w-4 h-4" />
                  <span>
                    {loading
                      ? t('common.saving')
                      : scheduleMode === 'now'
                        ? 'Salvar e publicar agora'
                        : 'Salvar agendamento'}
                  </span>
                </button>
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Category */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.category')}</h3>
              <select
                value={formData.category}
                onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">{t('common.selectCategory')}</option>
                {BLOG_CATEGORY_VALUES.map((cat) => (
                  <option key={cat} value={cat}>
                    {t(getBlogCategoryLabelKey(cat))}
                  </option>
                ))}
              </select>
            </div>

            {/* Targets */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Canais de publicação</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <BookOpen className="w-4 h-4" />
                    <span>Blog</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={formData.targets.blog}
                    onChange={() => toggleTarget('blog')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <Instagram className="w-4 h-4" />
                    <span>Instagram</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={formData.targets.instagram}
                    onChange={() => toggleTarget('instagram')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <Facebook className="w-4 h-4" />
                    <span>Facebook</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={formData.targets.facebook}
                    onChange={() => toggleTarget('facebook')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </label>
              </div>
            </div>

            {/* Scheduling */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Publicação</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="scheduleMode"
                    checked={scheduleMode === 'now'}
                    onChange={() => setScheduleMode('now')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Publicar agora</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="scheduleMode"
                    checked={scheduleMode === 'schedule'}
                    onChange={() => setScheduleMode('schedule')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Agendar para</span>
                </label>
                {scheduleMode === 'schedule' && (
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedesSociaisForm;
